import { ApiTypes } from "types"

import { urlPatternWithoutHttpPrefix } from "utils/regularExpressions"
import { partnerFormSchema } from "utils/validationShemas"

import type { ContractorPartnersFormCategory, PartnerPayloadSnapshot, PartnerValues } from "./types"

export const emptyPartnerRowTemplate: PartnerValues = {
  phone: "",
  useForTexts: false,
  phoneForTexts: "",
  websiteUrl: "",
  scheduleUrl: "",
  logo: null,
}

export const defaultPartnerValues = (): PartnerValues => ({ ...emptyPartnerRowTemplate })

const joiValidateOpts = { abortEarly: false }

export type PhoneField = "phone" | "phoneForTexts"
export type UrlField = "websiteUrl" | "scheduleUrl"
export type UrlErrors = Partial<Record<UrlField, string>>
export type PhoneErrors = Partial<Record<PhoneField, string>>

export const computePartnerFormErrors = (values: PartnerValues): { phone: PhoneErrors; url: UrlErrors } => {
  const result = partnerFormSchema.validate(
    {
      phone: values.phone,
      phoneForTexts: values.phoneForTexts,
      useForTexts: values.useForTexts,
      websiteUrl: values.websiteUrl,
      scheduleUrl: values.scheduleUrl,
    },
    joiValidateOpts,
  )
  const phone: PhoneErrors = {}
  const url: UrlErrors = {}
  if (result.error) {
    result.error.details.forEach(d => {
      const key = d.path[0] as string
      if (key === "phone" || key === "phoneForTexts") {
        phone[key as keyof PhoneErrors] = d.message
      }
      if (key === "websiteUrl" || key === "scheduleUrl") {
        url[key as keyof UrlErrors] = d.message
      }
    })
  }
  return { phone, url }
}

export const mergeFieldErrorsForBlur = <K extends string>(
  prev: Partial<Record<K, string>>,
  all: Partial<Record<K, string>>,
  blurred: K,
): Partial<Record<K, string>> => {
  const next = { ...prev }
  const message = all[blurred]
  if (message) next[blurred] = message
  else delete next[blurred]
  return next
}

export const phoneErrorsNonEmpty = (e: PhoneErrors) => !!(e.phone || e.phoneForTexts)
export const urlErrorsNonEmpty = (e: UrlErrors) => !!(e.websiteUrl || e.scheduleUrl)

export const phoneForSmsToUseForTexts = (v: boolean | 0 | 1 | string | undefined): boolean =>
  v === true || v === 1 || v === "1"

export const snapshotFromValues = (v: PartnerValues): PartnerPayloadSnapshot => ({
  websiteUrl: v.websiteUrl,
  scheduleUrl: v.scheduleUrl,
  phone: v.phone,
  phoneForTexts: v.phoneForTexts,
  useForTexts: v.useForTexts,
})

export const apiPartnerToValues = (p: ApiTypes.Model.ContractorPartnerProfileItem): PartnerValues => {
  const useForTexts = phoneForSmsToUseForTexts(p.phone_for_sms)
  return {
    phone: p.phone_call ?? "",
    useForTexts,
    phoneForTexts: useForTexts ? "" : (p.phone_sms ?? ""),
    websiteUrl: p.web_url ?? "",
    scheduleUrl: p.schedule_url ?? "",
    logo: null,
  }
}

export const isPartnerDirty = (
  key: string,
  v: PartnerValues,
  saved: Record<string, PartnerPayloadSnapshot>,
): boolean => {
  const s = saved[key]
  if (!s) return true
  return (
    s.websiteUrl !== v.websiteUrl ||
    s.scheduleUrl !== v.scheduleUrl ||
    s.phone !== v.phone ||
    s.phoneForTexts !== v.phoneForTexts ||
    s.useForTexts !== v.useForTexts ||
    v.logo != null
  )
}

const hasHttpScheme = /^https?:\/\//i

export const withHttpsIfSchemeless = (trimmed: string): string | null => {
  if (!trimmed || !urlPatternWithoutHttpPrefix.test(trimmed)) return null
  if (hasHttpScheme.test(trimmed)) return null
  return `https://${trimmed}`
}

export const buildPartnerFormData = (
  categoryId: number,
  values: PartnerValues,
  options: {
    sendLogoFieldAlways?: boolean
  },
): FormData => {
  const fd = new FormData()
  fd.append("partner_category_id", String(categoryId))
  fd.append("web_url", values.websiteUrl)
  fd.append("schedule_url", values.scheduleUrl)
  fd.append("phone_call", values.phone)
  fd.append("phone_for_sms", values.useForTexts ? "1" : "0")
  fd.append("phone_sms", values.useForTexts ? "" : values.phoneForTexts)
  if (values.logo) {
    fd.append("logo", values.logo, values.logo.name)
  } else if (options.sendLogoFieldAlways) {
    fd.append("logo", "")
  }
  return fd
}

export const buildVisibleCategoryIndex = (categories: ContractorPartnersFormCategory[]) => {
  const keys: string[] = []
  const keySet = new Set<string>()
  const categoryByKey = new Map<string, ContractorPartnersFormCategory>()
  for (const c of categories) {
    const key = String(c.id)
    keys.push(key)
    keySet.add(key)
    categoryByKey.set(key, c)
  }
  return { keys, keySet, categoryByKey }
}

export const hydratePartnerMapsFromList = (
  list: ApiTypes.Model.ContractorPartnerProfileItem[],
  keySet: Set<string>,
) => {
  const nextChecked: Record<string, boolean> = {}
  const nextValues: Record<string, PartnerValues> = {}
  const nextIds: Record<string, number> = {}
  const nextLogoUrls: Record<string, string> = {}
  const saved: Record<string, PartnerPayloadSnapshot> = {}

  keySet.forEach(k => {
    nextChecked[k] = false
  })

  list.forEach(p => {
    const key = String(p.partner_category.id)
    if (!keySet.has(key)) return
    nextChecked[key] = true
    const vals = apiPartnerToValues(p)
    nextValues[key] = vals
    if (typeof p.id === "number") nextIds[key] = p.id
    saved[key] = snapshotFromValues(vals)
    if (p.logo) nextLogoUrls[key] = p.logo
  })

  return { nextChecked, nextValues, nextIds, nextLogoUrls, saved }
}

type MutationRow = {
  key: string
  res: { data?: ApiTypes.Res.ContractorPartnerResource }
  values: PartnerValues
} | null

export const foldPartnerMutationResults = (results: readonly MutationRow[], assignIdsForKeys: Set<string>) => {
  const clearedByKey: Record<string, PartnerValues> = {}
  const logoUrlPatch: Record<string, string | undefined> = {}
  const newIds: Record<string, number> = {}
  const snapshots: Record<string, PartnerPayloadSnapshot> = {}

  for (const r of results) {
    if (!r) continue
    const id = r.res.data?.id
    if (id != null && assignIdsForKeys.has(r.key)) newIds[r.key] = id
    const cleared: PartnerValues = { ...r.values, logo: null }
    clearedByKey[r.key] = cleared
    snapshots[r.key] = snapshotFromValues(cleared)
    logoUrlPatch[r.key] = r.res.data?.logo
  }

  return { clearedByKey, logoUrlPatch, newIds, snapshots }
}
